import { KOKORO_VOICE_ID, MELOTTS_VOICE_ID, type NarrationEngine } from "./voices.js";

/**
 * Content-addressed cache keys.
 *
 * This module is deliberately free of browser and model imports so the Worker
 * producer and the client compute the same key. The Kokoro key format is
 * unchanged: the engine field is omitted for Kokoro, so the existing five seeds
 * and live uploads keep working.
 */

export const AUDIO_CACHE_URL_VERSION = "v1";
/** Bump when normalization or chunking changes the spoken script. */
export const AUDIO_NORMALIZATION_VERSION = 1;
/** The only artifact that is ever written for Kokoro. */
export const AUDIO_CACHE_ARTIFACT: AudioArtifact = "fp32";
/**
 * Cache codecs, best size first. Operator seeds are MP3; live Kokoro uploads
 * remain WAV because the browser has no MP3 encoder.
 */
export const AUDIO_CODECS = ["mp3", "wav"] as const;
/** What the MeloTTS producer actually returns: PCM in a WAV container. */
export const MELOTTS_CODEC = "wav";
/** Default key codec for the live WAV upload path. */
export const AUDIO_CODEC = "wav";
/** Both producers emit 24 kHz audio. */
export const AUDIO_SAMPLE_RATE = 24_000;
export const AUDIO_VOICE = KOKORO_VOICE_ID;
export const AUDIO_SPEED = 1;
export const AUDIO_CHUNK_PAUSE_SEC = 0.35;

export type AudioArtifact = "fp32" | "fp16" | "q8" | "none";

export interface AudioKeyInput {
  entryId: string;
  text: string;
  chunks?: string[];
  voice?: string;
  speed?: number;
  /** Kokoro only. MeloTTS is always `none`. */
  artifact?: AudioArtifact;
  engine?: NarrationEngine;
  sampleRate?: number;
  chunkPauseSec?: number;
  codec?: string;
  normalizationVersion?: number;
}

async function sha256Hex(text: string): Promise<string> {
  const bytes = new TextEncoder().encode(text);
  const digest = await crypto.subtle.digest("SHA-256", bytes);
  return [...new Uint8Array(digest)].map((byte) => byte.toString(16).padStart(2, "0")).join("");
}

/**
 * How a producer splits the script. Kokoro synthesizes each normalization
 * chunk with a short pause; MeloTTS takes the full spoken text in one request.
 */
export function audioKeyParts(
  engine: NarrationEngine,
  request: { text: string; chunks?: string[] },
  chunkPauseSec: number = AUDIO_CHUNK_PAUSE_SEC,
): { chunks: string[]; chunkPauseSec: number } {
  if (engine === "melotts") return { chunks: [request.text], chunkPauseSec: 0 };
  return { chunks: request.chunks ?? [request.text], chunkPauseSec };
}

/**
 * Content-addressed key for one dry narration.
 *
 * The text hash is the real invalidation: a changed description can never hit
 * an entry rendered from the old one. Voice, speed, pause, rate and codec are
 * explicit because each changes the waveform. The engine only appears for
 * MeloTTS so existing Kokoro keys stay byte-identical.
 */
export async function audioCacheKey(input: AudioKeyInput): Promise<string> {
  const engine = input.engine ?? "kokoro";
  const parts = audioKeyParts(engine, input, input.chunkPauseSec ?? AUDIO_CHUNK_PAUSE_SEC);
  const canonical = JSON.stringify({
    v: AUDIO_CACHE_URL_VERSION,
    n: input.normalizationVersion ?? AUDIO_NORMALIZATION_VERSION,
    id: input.entryId,
    text: input.text,
    chunks: parts.chunks,
    voice: input.voice ?? (engine === "melotts" ? MELOTTS_VOICE_ID : AUDIO_VOICE),
    speed: input.speed ?? AUDIO_SPEED,
    ...(engine === "kokoro" ? {} : { engine }),
    artifact: input.artifact ?? (engine === "melotts" ? "none" : AUDIO_CACHE_ARTIFACT),
    pauseMs: Math.round(parts.chunkPauseSec * 1000),
    sampleRate: input.sampleRate ?? AUDIO_SAMPLE_RATE,
    codec: input.codec ?? (engine === "melotts" ? MELOTTS_CODEC : AUDIO_CODEC),
  });
  return sha256Hex(canonical);
}

/** Keys for one entry in every codec the cache may hold, in preference order. */
export async function audioCacheKeys(
  input: Omit<AudioKeyInput, "codec">,
  codecs: readonly string[] = AUDIO_CODECS,
): Promise<Array<{ codec: string; key: string }>> {
  return Promise.all(
    codecs.map(async (codec) => ({ codec, key: await audioCacheKey({ ...input, codec }) })),
  );
}
